// Authentication service using Firebase Auth
// Initialize Firebase in main.tsx or a firebase.ts file first

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  updateProfile,
  onAuthStateChanged,
  type User as FirebaseUser
} from 'firebase/auth';
import type { User } from '@/types';
import { getRecaptchaToken } from '@/utils/recaptcha';

const isMobileBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iOS Safari / Chrome iOS / Android Chrome / Samsung — popup unreliable on all
  return /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
};

// This assumes firebase auth is initialized globally
// You can initialize it in main.tsx:
// import { initializeApp } from 'firebase/app';
// import { getAuth } from 'firebase/auth';
// const app = initializeApp(firebaseConfig);
// export const auth = getAuth(app);

let auth: any = null;

export const initAuth = (firebaseAuth: any) => {
  auth = firebaseAuth;
};

export const getAuth = () => auth;

// Registration with email/password
export const registerWithEmail = async (email: string, password: string, nombre: string) => {
  if (!auth) throw new Error('Auth not initialized');
  
  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    const user = credential.user;
    
    // Update profile with nombre
    await updateProfile(user, { displayName: nombre });
    
    // Crear la ficha del usuario (con reCAPTCHA). Antes no se miraba la
    // respuesta: si fallaba (p. ej. 429) la cuenta quedaba sin ficha, sin email
    // de confirmación del pedido y sin poder volver a entrar con email. Se
    // reintenta una vez; si aun así falla, /orders/create crea la ficha.
    for (let intento = 0; intento < 2; intento++) {
      const recaptchaToken = await getRecaptchaToken('register');
      const res = await fetch('/api/users/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          nombre: nombre,
          createdAt: new Date().toISOString(),
          recaptchaToken,
        })
      }).catch(() => null);
      if (res?.ok) break;
      if (intento === 0) await new Promise(r => setTimeout(r, 2500));
    }
    
    return {
      id: user.uid,
      email: user.email || '',
      nombre: nombre,
      rol: 'cliente',
      foto: user.photoURL || undefined,
      createdAt: new Date()
    } as User;
  } catch (err: any) {
    throw new Error(err.message || 'Error registering user');
  }
};

// Server-side login audit (fire & forget). El backend registra el evento en
// security_events con la IP y geo. Solo se llama tras un login Firebase Auth
// exitoso porque Firebase no nos notifica logins server-side.
async function logLoginToBackend(idToken: string, method: 'email' | 'google') {
  try {
    await fetch('/api/auth/log-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, method }),
    });
  } catch { /* no romper el flujo de login si esto falla */ }
}

// Login with email/password
export const loginWithEmail = async (email: string, password: string) => {
  if (!auth) throw new Error('Auth not initialized');

  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const user = credential.user;

    // Fetch user doc from Firestore
    const res = await fetch(`/api/users/${user.uid}`);
    if (!res.ok) throw new Error('User not found in database');

    user.getIdToken().then(t => logLoginToBackend(t, 'email')).catch(() => {});
    return await res.json() as User;
  } catch (err: any) {
    throw new Error(err.message || 'Error logging in');
  }
};

// Helper: ensure user doc exists, return User
async function syncGoogleUserDoc(user: FirebaseUser): Promise<User> {
  const res = await fetch(`/api/users/${user.uid}`);
  if (!res.ok) {
    const recaptchaToken = await getRecaptchaToken('register');
    const createRes = await fetch('/api/users/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid: user.uid,
        email: user.email,
        nombre: user.displayName || 'User',
        foto: user.photoURL,
        createdAt: new Date().toISOString(),
        recaptchaToken,
      }),
    });
    if (!createRes.ok) {
      const errBody = await createRes.json().catch(() => ({}));
      throw new Error(errBody.error || 'Error registering user');
    }
    return await createRes.json();
  }
  return await res.json();
}

// Login with Google — usa redirect en móvil (popup falla en iOS/Android),
// popup en desktop. En el caso redirect, el resultado se recoge al cargar
// la app vía consumeGoogleRedirectResult().
export const loginWithGoogle = async () => {
  if (!auth) throw new Error('Auth not initialized');
  const provider = new GoogleAuthProvider();
  // Forzar el selector de cuentas siempre (sin esto Google auto-selecciona la
  // sesión activa en el navegador y no permite cambiar). Imprescindible para
  // navegadores donde varios usuarios comparten dispositivo o el admin tiene
  // varias cuentas Google.
  provider.setCustomParameters({ prompt: 'select_account' });
  if (isMobileBrowser()) {
    // Marca que estamos en flujo de redirect para que el resultado se procese al volver
    sessionStorage.setItem('funcomunion_google_redirect', '1');
    await signInWithRedirect(auth, provider);
    // El navegador navega fuera; nada se ejecuta después
    return null;
  }
  try {
    const credential = await signInWithPopup(auth, provider);
    const userData = await syncGoogleUserDoc(credential.user);
    if (!userData?.id) throw new Error('Invalid user data received');
    credential.user.getIdToken().then(t => logLoginToBackend(t, 'google')).catch(() => {});
    return userData;
  } catch (err: any) {
    // Fallback: si el popup falla por bloqueo del navegador, prueba con redirect
    const code = err?.code || '';
    if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      sessionStorage.setItem('funcomunion_google_redirect', '1');
      await signInWithRedirect(auth, provider);
      return null;
    }
    throw new Error(err.message || 'Error logging in with Google');
  }
};

// Llamar al cargar la app: si volvemos de un redirect de Google, procesa el resultado.
// Devuelve el User si había sesión nueva, null si no.
export const consumeGoogleRedirectResult = async (): Promise<User | null> => {
  if (!auth) return null;
  const flag = sessionStorage.getItem('funcomunion_google_redirect');
  try {
    let firebaseUser: FirebaseUser | null = null;
    try {
      const result = await getRedirectResult(auth);
      if (result?.user) {
        firebaseUser = result.user;
        console.info('[auth] Google redirect resolved via getRedirectResult', { uid: firebaseUser.uid, email: firebaseUser.email });
      }
    } catch (e) {
      console.warn('[auth] getRedirectResult error:', e);
    }
    // Si volvemos de un redirect (flag set) pero getRedirectResult no devuelve user
    // (puede pasar tras hard reload o cookie issues), revisar auth.currentUser que
    // Firebase mantiene en local persistence
    if (!firebaseUser && flag && auth.currentUser) {
      firebaseUser = auth.currentUser;
      console.info('[auth] Google redirect picked up via auth.currentUser fallback', { uid: firebaseUser.uid });
    }
    if (!firebaseUser) {
      if (flag) {
        sessionStorage.removeItem('funcomunion_google_redirect');
        console.warn('[auth] Google redirect flag set but no user resolved');
      }
      return null;
    }
    sessionStorage.removeItem('funcomunion_google_redirect');
    const userData = await syncGoogleUserDoc(firebaseUser);
    if (userData?.id) {
      try { localStorage.setItem('funcomunion_user', JSON.stringify(userData)); } catch {}
    }
    firebaseUser.getIdToken().then(t => logLoginToBackend(t, 'google')).catch(() => {});
    return userData;
  } catch (err) {
    if (flag) sessionStorage.removeItem('funcomunion_google_redirect');
    console.error('[auth] consumeGoogleRedirectResult error:', err);
    return null;
  }
};

// Login with phone number — resolves phone to email, then logs in
export const loginWithPhone = async (phone: string, password: string) => {
  if (!auth) throw new Error('Auth not initialized');

  // Ask backend to resolve phone → email
  const resolveRes = await fetch('/api/users/resolve-phone', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  if (!resolveRes.ok) {
    const err = await resolveRes.json().catch(() => ({}));
    throw new Error(err.error || 'No hay cuenta asociada a este telefono');
  }
  const { email, nombre } = await resolveRes.json();

  // Try login with the resolved email + password
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const user = credential.user;
    const res = await fetch(`/api/users/${user.uid}`);
    if (!res.ok) throw new Error('Usuario no encontrado');
    return await res.json() as User;
  } catch (err: any) {
    const code = err?.code || '';
    // Account exists but was created with Google (no password set)
    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
      throw new Error(`La cuenta de ${nombre || email} se creo con Google. Usa "Continuar con Google" para entrar.`);
    }
    throw new Error(err.message || 'Error al iniciar sesion');
  }
};

// Detect if input looks like a phone number (any string of 6+ digits, no @)
export const isPhoneInput = (input: string): boolean => {
  if (input.includes('@')) return false;
  const cleaned = input.replace(/[\s\-\+\(\)]/g, '');
  return /^\d{6,15}$/.test(cleaned);
};

// Password reset — uses our branded backend email instead of Firebase default
export const resetPassword = async (email: string) => {
  try {
    const res = await fetch('/api/users/send-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) throw new Error('Error al enviar el correo');
    return true;
  } catch (err: any) {
    throw new Error(err.message || 'Error sending reset email');
  }
};

// Logout
export const logout = async () => {
  if (!auth) throw new Error('Auth not initialized');
  
  try {
    await signOut(auth);
    return true;
  } catch (err: any) {
    throw new Error(err.message || 'Error logging out');
  }
};

// Get current user
export const getCurrentUser = async (): Promise<User | null> => {
  if (!auth) return null;
  
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (!firebaseUser) {
        resolve(null);
        unsubscribe();
        return;
      }
      
      try {
        const res = await fetch(`/api/users/${firebaseUser.uid}`);
        const userData = await res.json() as User;
        resolve(userData);
      } catch (err) {
        resolve(null);
      }
      unsubscribe();
    });
  });
};

// Subscribe to auth state changes
export const onAuthChanged = (callback: (user: User | null) => void) => {
  if (!auth) {
    callback(null);
    return () => {};
  }
  
  return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
    if (!firebaseUser) {
      callback(null);
      return;
    }
    
    try {
      const res = await fetch(`/api/users/${firebaseUser.uid}`);
      const userData = await res.json() as User;
      callback(userData);
    } catch (err) {
      callback(null);
    }
  });
};

// Get ID token for API calls — waits for Firebase auth to finish initializing
export const getIdToken = (): Promise<string | null> => {
  if (!auth) return Promise.resolve(null);
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();
      if (!user) { resolve(null); return; }
      try {
        resolve(await user.getIdToken());
      } catch {
        resolve(null);
      }
    });
  });
};
